import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { financeApi } from '@/api/finance.api';
import { showApiError } from '@/utils/apiError';
import type { FinWorkflowDefinition, FinDunningNotice } from '@/types/finance';
import { Button } from '@/components/ui/Button';
import { ButtonLink } from '@/components/ui/ButtonLink';
import { notify } from '@/core/toast/notify';

export function FinanceWorkflowsPage() {
  const [workflows, setWorkflows] = useState<FinWorkflowDefinition[]>([]);
  const [notices, setNotices] = useState<FinDunningNotice[]>([]);
  const { rows: paginatedNotices, tablePaginationProps: noticesPaginationProps } = useClientPaginatedRows(notices);

  const load = () => {
    financeApi.listWorkflows().then(setWorkflows).catch((e) => showApiError(e, 'Failed to load workflows'));
    financeApi.listDunningNotices().then(setNotices).catch(() => setNotices([]));
  };

  useEffect(() => {
    load();
  }, []);

  const runDunning = async () => {
    try {
      const r = await financeApi.runDunning();
      notify.success(`Sent ${r.sent} dunning notices`);
      load();
    } catch (e) {
      showApiError(e, 'Dunning run failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Workflows & collections"
        description="Configurable approval workflows and AR dunning"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Finance', to: '/finance' }, { label: 'Workflows' }]}
        actions={
          <div className="flex gap-2">
            <ButtonLink to="/finance/workflows/designer" variant="secondary" size="sm">
              Workflow designer
            </ButtonLink>
            <Button variant="secondary" onClick={() => void runDunning()}>Run dunning</Button>
          </div>
        }
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card p-4">
          <h3 className="font-semibold mb-2">Workflow definitions</h3>
          <ul className="text-sm space-y-1">
            {workflows.map((w) => (
              <li key={w.id}>{w.workflowCode} — {w.workflowName} ({w.entityType}, {w.routingType})</li>
            ))}
            {workflows.length === 0 && <li className="text-slate-500">No workflows configured yet.</li>}
          </ul>
        </div>
        <div className="card overflow-x-auto">
          <h3 className="font-semibold p-3 border-b">Dunning notices</h3>
          <table className="w-full text-sm">
            <tbody>
              {paginatedNotices.map((n) => (
                <tr key={n.id} className="border-b">
                  <td className="p-2">Customer #{n.customerId}</td>
                  <td className="p-2">L{n.dunningLevel}</td>
                  <td className="p-2">{n.noticeDate}</td>
                  <td className="p-2 text-right font-mono">{n.openAmount?.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination {...noticesPaginationProps} hasRows={notices.length > 0} />
        </div>
      </div>
    </div>
  );
}
